/* ============================================================
   RUSH REPUBLIC — LEAD QUALITY SCORE
   Centralized scoring engine (client-side live preview).

   This MUST stay in exact lockstep with backend/leadscore/scoring.py —
   the API's persisted score is the source of truth; this copy exists only
   so the Add/Edit Lead form can show the score updating instantly while
   typing, without a round trip per keystroke.
   ============================================================ */

export function validatePhone(phone) {
  if (!phone) return false;
  const cleaned = phone.replace(/[\s\-()+]/g, '');
  return /^[0-9]{7,15}$/.test(cleaned);
}

function normalizeText(value) {
  if (!value) return '';
  return value.toLowerCase().trim().replace(/[^a-z0-9\s]/g, '').trim();
}

export function locationsMatch(brandLoc, leadLoc) {
  if (!brandLoc || !leadLoc) return null;
  const bParts = normalizeText(brandLoc).split(/\s*,\s*|\s+/);
  const lNorm = normalizeText(leadLoc);
  for (const part of bParts) {
    if (part.length > 2 && lNorm.includes(part)) return true;
  }
  const lParts = lNorm.split(/\s*,\s*|\s+/);
  const bNorm = normalizeText(brandLoc);
  for (const part of lParts) {
    if (part.length > 2 && bNorm.includes(part)) return true;
  }
  return false;
}

export function requirementMatches(brandProduct, leadService) {
  if (!brandProduct || !leadService) return null;
  const bWords = normalizeText(brandProduct).split(/\s+/).filter((w) => w.length > 2);
  const lNorm = normalizeText(leadService);
  for (const w of bWords) {
    if (lNorm.includes(w)) return true;
  }
  const lWords = normalizeText(leadService).split(/\s+/).filter((w) => w.length > 2);
  const bNorm = normalizeText(brandProduct);
  for (const w of lWords) {
    if (bNorm.includes(w)) return true;
  }
  return false;
}

function normalizeBudget(str) {
  if (!str) return null;
  const match = str.replace(/[,\s]/g, '').match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
}

function verificationToScore(value, max) {
  switch (value) {
    case 'satisfied':
    case 'yes':
      return max;
    case 'partial':
    case 'partially_satisfied':
      return max / 2;
    default:
      return 0;
  }
}

function resolveVerification(autoValue, userValue, max) {
  if (userValue && userValue !== 'auto') return verificationToScore(userValue, max);
  if (autoValue !== null && autoValue !== undefined) return verificationToScore(autoValue, max);
  return 0;
}

function resolveVerificationSimple(userValue, max) {
  if (!userValue) return 0;
  return verificationToScore(userValue, max);
}

function scoreToStatus(earned, max) {
  if (earned >= max) return 'satisfied';
  if (earned > 0) return 'partial';
  return 'failed';
}

export function getClassification(score) {
  if (score >= 80) return { label: 'High Quality', cls: 'high', desc: 'Strong target fit and purchase intent' };
  if (score >= 60) return { label: 'Qualified', cls: 'qualified', desc: 'Meets most qualification criteria' };
  if (score >= 40) return { label: 'Low Quality / Nurture', cls: 'low', desc: 'Some potential but requires nurturing' };
  return { label: 'Invalid / Irrelevant', cls: 'invalid', desc: 'Poor fit, fake, unreachable or irrelevant' };
}

/**
 * calculateLeadQualityScore(lead, brand)
 * Returns: { marketingScore, salesScore, totalScore, classification, checks }
 */
export function calculateLeadQualityScore(lead, brand) {
  const checks = {};

  // ----- A. CONTACTABILITY (20 pts) -----
  const phoneValid = validatePhone(lead.phone);
  const phoneScore = resolveVerification(phoneValid ? 'satisfied' : 'not_satisfied', lead.verify_phone_valid, 10);
  checks.phoneValid = { label: 'Valid Phone Number', earned: phoneScore, max: 10, status: scoreToStatus(phoneScore, 10), auto: phoneValid };

  const reachScore = resolveVerificationSimple(lead.verify_phone_reachable, 10);
  checks.phoneReachable = { label: 'Phone Reachable', earned: reachScore, max: 10, status: scoreToStatus(reachScore, 10) };

  // ----- B. TARGET FIT (30 pts) -----
  const autoLocMatch = locationsMatch(brand.target_location, lead.location);
  const locationScore = resolveVerification(
    autoLocMatch === true ? 'satisfied' : autoLocMatch === false ? 'not_satisfied' : null,
    lead.verify_location, 10
  );
  checks.location = { label: 'Correct Location / Serviceable Area', earned: locationScore, max: 10, status: scoreToStatus(locationScore, 10), auto: autoLocMatch };

  let customerMatch = null;
  if (brand.target_customer_type && lead.customer_type) {
    customerMatch = brand.target_customer_type === 'Both' ? true : brand.target_customer_type === lead.customer_type;
  }
  const customerScore = resolveVerification(
    customerMatch === true ? 'satisfied' : customerMatch === false ? 'not_satisfied' : null,
    lead.verify_customer_type, 10
  );
  checks.customerType = { label: 'Matches Target Customer Profile', earned: customerScore, max: 10, status: scoreToStatus(customerScore, 10), auto: customerMatch };

  const autoReqMatch = requirementMatches(brand.primary_product, lead.service_required);
  const reqScore = resolveVerification(
    autoReqMatch === true ? 'satisfied' : autoReqMatch === false ? 'not_satisfied' : null,
    lead.verify_requirement, 10
  );
  checks.requirement = { label: 'Relevant Requirement', earned: reqScore, max: 10, status: scoreToStatus(reqScore, 10), auto: autoReqMatch };

  // ----- C. PURCHASE INTENT (30 pts) -----
  const actualReqScore = resolveVerificationSimple(lead.verify_actual_requirement, 10);
  checks.actualRequirement = { label: 'Actual Requirement Confirmed', earned: actualReqScore, max: 10, status: scoreToStatus(actualReqScore, 10) };

  let budgetTimelineMatch = null;
  if (brand.ideal_budget && lead.budget) {
    const bLow = normalizeBudget(brand.ideal_budget);
    const lLow = normalizeBudget(lead.budget);
    if (bLow !== null && lLow !== null) budgetTimelineMatch = lLow >= bLow * 0.5;
  }
  const budgetScore = resolveVerification(
    budgetTimelineMatch === true ? 'satisfied' : budgetTimelineMatch === false ? 'not_satisfied' : null,
    lead.verify_budget_timeline, 10
  );
  checks.budgetTimeline = { label: 'Suitable Timeline / Budget', earned: budgetScore, max: 10, status: scoreToStatus(budgetScore, 10), auto: budgetTimelineMatch };

  const interestScore = resolveVerificationSimple(lead.verify_shows_interest, 10);
  checks.showsInterest = { label: 'Shows Interest', earned: interestScore, max: 10, status: scoreToStatus(interestScore, 10) };

  // ----- D. SALES PROGRESSION (20 pts) -----
  const questionsScore = resolveVerificationSimple(lead.verify_relevant_questions, 5);
  checks.relevantQuestions = { label: 'Asks Relevant Questions', earned: questionsScore, max: 5, status: scoreToStatus(questionsScore, 5) };

  const apptScore = lead.sales_appointment_scheduled === 'yes' ? 5 : 0;
  checks.appointment = { label: 'Appointment / Meeting Scheduled', earned: apptScore, max: 5, status: scoreToStatus(apptScore, 5) };

  const quotScore = lead.sales_opportunity_generated === 'yes' ? 5 : 0;
  checks.opportunity = { label: 'Opportunity / Quotation Generated', earned: quotScore, max: 5, status: scoreToStatus(quotScore, 5) };

  const convScore = lead.sales_converted === 'yes' ? 5 : 0;
  checks.converted = { label: 'Converted', earned: convScore, max: 5, status: scoreToStatus(convScore, 5) };

  // ----- TOTALS -----
  const marketingScore = Math.round(
    phoneScore + reachScore + locationScore + customerScore + reqScore + actualReqScore + budgetScore + interestScore
  );
  const salesScore = Math.round(questionsScore + apptScore + quotScore + convScore);
  const totalScore = marketingScore + salesScore;

  return {
    marketingScore,
    salesScore,
    totalScore,
    classification: getClassification(totalScore),
    checks,
  };
}
